import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { supabase } from "../supabase";
import "./KPIDashboardScreen.css";
import ashleyMissionControl from "../assets/mission-control/ashley-mission-control.jpg";
import ashleyGuide from "../assets/mission-control/ashley-guide.png";
import ashleyGuideVideo from "../assets/mission-control/ashleyguide1.mp4";
import ashleyUpdatesVideo from "../assets/mission-control/ashleyupdates.mp4";
import ringPng from "../assets/mission-control/ring.png";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { useDashboardCarouselSwipe } from "../hooks/useDashboardCarouselSwipe";
import { useLongPressAction } from "../hooks/useLongPressAction";
import { SnapshotMetricCard } from "./dashboard/SnapshotMetricCard";
import { ProjectHoursDonutChart } from "./dashboard/ProjectHoursDonutChart";
import { ActiveCrewListModal } from "./dashboard/ActiveCrewListModal";
import { ViewportPortal } from "./shared/ViewportDialog";
import { ActiveSessionMaterialsSection } from "./dashboard/ActiveSessionMaterialsSection";
import { GoogleVisibilityEntryCard } from "./googleVisibility/GoogleVisibilityEntryCard";
import { getOrderedKpiWidgetIds } from "../config/dashboardWidgets";
import { loadDashboardWidgetOrder } from "../helpers/dashboardWidgetOrder";
import { isHomeSwipeEnabled } from "../helpers/dashboard/homeDashboardLayout";
import {
  formatWeekRangeLabel,
  getPayrollWeekBoundsFromSettings,
  toLocalDateIso,
} from "../helpers/payrollWeek";
import { formatSessionShiftLabel } from "../helpers/dateTimeHelpers";
import {
  computePayrollPeriodTotals,
  sumHours,
} from "../helpers/payrollHelpers";
import { useOrganization } from "../context/OrganizationContext";
import { launchAshleyGuide } from "../helpers/ashleyGuideLauncher";
import { useProductEducationOptional } from "../productEducation/ProductEducationProvider";
import { LEGACY_DASHBOARD_GUIDE_KEY } from "../productEducation/shared/types";
import { usePermissions } from "../hooks/usePermissions";
import { CAPABILITY, isEmployee } from "../helpers/permissions";
import { deliverExecutiveBriefing } from "../ashley/briefing/buildExecutiveBriefing";
import {
  applyOperationalActiveSessionOrgScope,
  applyOperationalTimeLogOrgScope,
  applyUserIdsToQuery,
  resolveOperationalOrganizationId,
  resolveScopedUserIdsForQuery,
  selectOperationalTimeLogs,
} from "../helpers/scopedQueries";
import { loadScopedProjectsByIds } from "../helpers/operationalQueryAdapters";
import {
  aggregateProjectHoursFromLogs,
  reconcileProjectHoursRows,
} from "../helpers/dashboardProjectHours";
import { formatProjectHoursDisplay } from "../helpers/projectHoursChart";
import { isOffline } from "../helpers/connectivity";
import { APP_SESSION_STATE_CHANGED_EVENT } from "../helpers/appSessionStateEvents";
import { runMicRecognitionSmokeTest, MIC_SMOKE_TEST_ENABLED } from "../debug/micRecognitionSmokeTest";
import { devLog } from "../helpers/devLog";
import { SyncStatusIndicator } from "./SyncStatusIndicator";
import { useIsDesktopViewport } from "../hooks/useIsDesktopViewport";
import { DesktopCommandDashboard } from "./dashboard/desktop/DesktopCommandDashboard";
import { HomeSwipeAffordance } from "./DashboardPositionIndicator";

// Temporary: auto startup briefing disabled pending Settings → Startup Briefing (default: Off).
// Set to true to re-enable automatic briefing on dashboard load.
const STARTUP_BRIEFING_AUTO_ENABLED = false;

// ── Helpers ────────────────────────────────────────────────

// Format a timestamp string (ISO or time-only) to HH:MM display
function fmtTime(ts) {
  if (!ts) return null;
  try {
    return new Date(ts).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return null;
  }
}
function getElapsedTime(sessionStart) {
  if (!sessionStart) return "00:00";

  const diff = Math.floor(
    (Date.now() - new Date(sessionStart).getTime()) / 1000,
  );

  const hrs = Math.floor(diff / 3600);
  const mins = Math.floor((diff % 3600) / 60);

  return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
}

// Format date_iso (YYYY-MM-DD) to "May 27" style
function fmtDateIso(dateIso) {
  if (!dateIso) return "";
  try {
    const d = new Date(dateIso + "T00:00:00");
    return d.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
  } catch {
    return dateIso;
  }
}

// Get short weekday from date_iso
function getDayLabel(dateIso) {
  try {
    const d = new Date(dateIso + "T00:00:00");
    return d.toLocaleDateString("en-CA", { weekday: "short" });
  } catch {
    return "";
  }
}

// Returns YYYY-MM-DD for a Date object (local calendar)
function toIsoDate(d) {
  return toLocalDateIso(d);
}
function formatHoursMinutes(decimalHours) {
  return formatProjectHoursDisplay(decimalHours);
}

function getGreeting() {
  const hour = new Date().getHours();

  if (hour < 12) {
    return "Good morning,";
  }

  if (hour < 18) {
    return "Good afternoon,";
  }

  return "Good evening,";
}
function speakGreeting(text) {
  if (!window.speechSynthesis) return;

  const speak = () => {
    const voices = window.speechSynthesis.getVoices();

    const ashleyVoice = voices.find(
      (v) => v.name === "Google UK English Female",
    );

    const utterance = new SpeechSynthesisUtterance(text);

    if (ashleyVoice) {
      utterance.voice = ashleyVoice;
    }

    utterance.rate = 1;
    utterance.pitch = 0.85;
    utterance.volume = 1;

    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
  };

  const voices = window.speechSynthesis.getVoices();

  if (voices.length > 0) {
    speak();
  } else {
    window.speechSynthesis.onvoiceschanged = () => {
      speak();
    };
  }
}

// ── Bar chart (7-day trend) ────────────────────────────────
function WeekBarChart({ data }) {
  const max = Math.max(...data.map((d) => d.hrs), 0.1);
  return (
    <div className="kpi-bar-chart">
      {data.map((d, i) => (
        <div key={i} className="kpi-bar-col">
          <div className="kpi-bar-track">
            <div
              className="kpi-bar-fill"
              style={{ height: `${(d.hrs / max) * 100}%` }}
            />
          </div>
          <span className="kpi-bar-label">{d.day}</span>
        </div>
      ))}
    </div>
  );
}

// ── Main screen ────────────────────────────────────────────
export function KPIDashboardScreen({
  onCarouselNavigate,
  onOpenNavigator,
  onOpenEstimateHubWithStatus,
  onOpenFinance,
  onOpenAccountsReceivable,
  onOpenOverdueInvoices,
  onOpenAccountsPayable,
  onOpenClients,
  onOpenProjects,
  onOpenProjectsWithOverdueTasks,
  onOpenTimeLogs,
  onOpenTimeLog,
  onOpenTimeLogsThisWeek,
  onOpenAshleyHome,
  onOpenGoogleVisibility,
  onOpenPlanning,
  quickActions,
  userName,
  currentUser,
  onOpenSession,
  onOpenProjectHours,
  onOpenProjectHoursRow,
  onOpenUserPreferences,
}) {
  const { can, directReportIds, orgPolicies } = usePermissions();
  const { settings } = useOrganization();
  const productEducation = useProductEducationOptional();
  const showActiveCrew = can(CAPABILITY.DASHBOARD_ACTIVE_CREW);
  const isDesktop = useIsDesktopViewport();
  const canViewMaterialPricing = can(CAPABILITY.MATERIAL_CATALOG_VIEW_PRICING);
  const [homeArranging, setHomeArranging] = useState(false);

  const [guideDriver, setGuideDriver] = useState(null);
  const { ref: carouselRef, style: carouselTouchStyle } =
    useDashboardCarouselSwipe({
      onNavigate: onCarouselNavigate,
      onOpenNavigator,
      enabled: isHomeSwipeEnabled({ arranging: homeArranging }),
    });
  const { ref: ashleyHomeLauncherRef } = useLongPressAction({
    onLongPress: onOpenNavigator,
    enabled: Boolean(onOpenNavigator),
  });

  const startDashboardGuide = () => {
    const driverObj = driver({
      stagePadding: 8,
      showProgress: false,
      steps: [
        {
          element: ".guide-help-button",
          popover: {
            title: "Need Help?",
            description:
              "Tap the Guide button anytime for a quick tour of CrewPilot.\n\nOr tap anywhere on the screen to explore on your own.",
            side: "top",
            align: "center",
          },
        },
        {
          element: ".floating-home-button",
          popover: {
            title: "Quick Menu",
            description:
              "Tap the Ashley button to open your quick menu and jump to modules.",
            side: "left",
            align: "center",
          },
        },

        {
          element: ".guide-command-center",
          popover: {
            title: "Time Tracking",
            description:
              "This is where you start and stop time, manage breaks, and control your work session.",
            side: "left",
            align: "center",
          },
        },
        {
          element: ".guide-select-project",
          popover: {
            title: "Select Project",
            description:
              "Choose the project you are about to work on before starting your session.",
            side: "bottom",
            align: "center",
          },
        },
        {
          element: ".guide-payroll-type",
          popover: {
            title: "Payroll Type",
            description: "Select the type of work being performed.",
            side: "top",
            align: "center",
          },
        },
        {
          element: ".guide-start-session",
          popover: {
            title: "Start Session",
            description:
              "Select your project and press Start Session to begin tracking your work day.\n\nAfter starting your session, click Next to continue the guide.",
            side: "bottom",
            align: "center",
          },
        },
        {
          element: ".guide-stop-session",
          popover: {
            title: "Stop Session",
            description:
              "Select your project and press Stop Session to begin tracking your work day.",
            side: "top",
            align: "center",
          },
        },
        {
          element: ".guide-submit-session",
          popover: {
            title: "Submit Session",
            description:
              "Fill in the work completed, check the Work Type, add any notes, then press Submit Session to save your work log.",
            side: "top",
            align: "center",
          },
        },
        {
          element: ".floating-home-button",
          popover: {
            title: "Quick Menu",
            description:
              "Tap the Ashley button to open your quick menu and jump to modules.",
            side: "left",
            align: "center",
          },
        },
        {
          element: ".guide-estimates-hub",
          popover: {
            title: "Estimates",
            description:
              "Open the Estimate Hub anytime to review drafts, sent quotes, and converted projects.",
            side: "left",
            align: "center",
          },
        },
      ],
    });

    setGuideDriver(driverObj);
    driverObj.drive();
  };

  const runLegacyDashboardGuide = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    try {
      localStorage.setItem(LEGACY_DASHBOARD_GUIDE_KEY, "true");
    } catch {
      // ignore storage failures
    }

    launchAshleyGuide();

    setTimeout(() => {
      startDashboardGuide();
    }, 300);
  };

  // Legacy Driver.js tour is manual-only (Guide button / explicit request).
  // Do not auto-launch on dashboard visit.

  useEffect(() => {
    const handleMissionControlOpened = () => {
      if (guideDriver) {
        setTimeout(() => {
          guideDriver.moveNext();
        }, 350);
      }
    };
    const handleMissionRecordingStarted = () => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      setTimeout(() => {
        setMissionRecording(true);
      }, 250);
    };
    const handleMissionRecordingStopped = () => {
      setMissionRecording(false);
    };
    
    const handleAshleyBriefingStart = () => {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });

  

  if (window.lastAshleyBriefing) {
    speakGreeting(window.lastAshleyBriefing);
  }
};
    const handleCommandCenterOpened = () => {
      if (guideDriver) {
        setTimeout(() => {
          guideDriver.moveNext();
        }, 350);
      }
    };

    window.addEventListener(
      "mission-control-opened",
      handleMissionControlOpened,
    );

    window.addEventListener("command-center-opened", handleCommandCenterOpened);
    window.addEventListener(
      "mission-recording-started",
      handleMissionRecordingStarted,
    );
    window.addEventListener(
      "mission-recording-stopped",
      handleMissionRecordingStopped,
    );
    window.addEventListener(
  "ashley-briefing-start",
  handleAshleyBriefingStart,
);

    return () => {
      window.removeEventListener(
        "mission-control-opened",
        handleMissionControlOpened,
      );
      window.removeEventListener(
        "mission-recording-started",
        handleMissionRecordingStarted,
      );
      window.removeEventListener(
        "mission-recording-stopped",
        handleMissionRecordingStopped,
      );
      window.removeEventListener(
  "ashley-briefing-start",
  handleAshleyBriefingStart,
);
      window.removeEventListener(
        "command-center-opened",
        handleCommandCenterOpened,
      );
    };
  }, [guideDriver]);

  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsOffline, setMetricsOffline] = useState(false);
  const [summary, setSummary] = useState({
    weekHrs: "0.0",
    activeProjects: 0,
  });
  const [weeklySnapshot, setWeeklySnapshot] = useState({
    weekHrs: "0.0",
    overtimeHrs: "0.0",
    estimatedGrossPay: "$0.00",
    weekRangeLabel: "",
  });
  const [crewSnapshot, setCrewSnapshot] = useState({
    crewHoursWeek: "0.0",
    unpaidEntries: 0,
  });
  const [weekTrend, setWeekTrend] = useState([]);
  const [projectHours, setProjectHours] = useState([]);
  const [recentLogs, setRecentLogs] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [activeCrew, setActiveCrew] = useState([]);
  const [unpaidPayrollCount, setUnpaidPayrollCount] = useState(0);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [selectedCrew, setSelectedCrew] = useState(null);
  const [sessionMaterialsRefreshKey, setSessionMaterialsRefreshKey] = useState(0);
  const [activeCrewListFilter, setActiveCrewListFilter] = useState(null);
  const [missionRecording, setMissionRecording] = useState(false);
  const [missionSeconds, setMissionSeconds] = useState(0);

  const [now] = useState(new Date());
  const briefingInFlightRef = useRef(false);

  const triggerAshleyBriefing = useCallback(async () => {
    if (briefingInFlightRef.current) {
      return;
    }

    briefingInFlightRef.current = true;

    try {
      await deliverExecutiveBriefing({
        actor: currentUser,
        displayName: userName,
        orgSettings: settings,
        directReportIds,
        orgPolicies,
        can,
      });
    } finally {
      briefingInFlightRef.current = false;
    }
  }, [
    can,
    currentUser,
    directReportIds,
    orgPolicies,
    settings,
    userName,
  ]);

  useEffect(() => {
    if (!STARTUP_BRIEFING_AUTO_ENABLED) {
      return;
    }

    if (sessionStorage.getItem("cpGreetingPlayed")) {
      return;
    }

    if (metricsLoading) {
      return;
    }

    setTimeout(() => {
      triggerAshleyBriefing();
    }, 1000);

    sessionStorage.setItem("cpGreetingPlayed", "true");
  }, [metricsLoading, triggerAshleyBriefing]);
  useEffect(() => {
    window.scrollTo(0, 0);

    if (currentUser?.isGuest) {
      const guestLogs = JSON.parse(
        localStorage.getItem("guest_recent_activity") || "[]",
      );
      const seedLogs = [
        {
          dateIso: "2026-06-04",
          workCompleted: "Completed Borehole #17",
          notes:
            "Mike Tremblay completed drilling, sample collection and site documentation.",
          projectName: "Highway 17 Soil Investigation",
          hrs: "8.0",
          employeeName: "Mike Tremblay",
          startTime: "07:00",
          endTime: "15:00",
        },
        {
          dateIso: "2026-06-04",
          workCompleted: "Prepared Client Update Package",
          notes:
            "Sarah Martin reviewed field reports and prepared the client progress summary.",
          projectName: "Municipal Water Study",
          hrs: "2.5",
          employeeName: "Sarah Martin",
          startTime: "08:30",
          endTime: "11:00",
        },
        {
          dateIso: "2026-06-03",
          workCompleted: "Equipment Inspection Completed",
          notes:
            "Chris Fournier completed fleet inspection and maintenance checklist.",
          projectName: "Fleet Operations",
          hrs: "1.5",
          employeeName: "Chris Fournier",
          startTime: "06:30",
          endTime: "08:00",
        },
      ];

      const demoLogs = [...guestLogs, ...seedLogs];

      setRecentLogs(demoLogs);
      setProjectHours([
        {
          name: "Highway 17 Soil Investigation",
          hrs: 42.5,
        },
        {
          name: "Municipal Water Study",
          hrs: 38.0,
        },
        {
          name: "Fleet Operations",
          hrs: 31.5,
        },
        {
          name: "Smith Quarry Investigation",
          hrs: 30.0,
        },
      ]);

      const demoWeekLogs = [
        {
          supabase_user_id: "guest-demo",
          employee: userName || "Demo User",
          total_hours: 38.5,
          hourly_rate: 42.662337662,
          date_iso: toLocalDateIso(new Date()),
        },
      ];
      const demoPayrollTotals = computePayrollPeriodTotals(
        demoWeekLogs,
        settings,
      );
      const demoWeekHrs = demoPayrollTotals.totalHours.toFixed(1);
      const { weekStartStr, weekEndStr } = getPayrollWeekBoundsFromSettings(
        new Date(),
        settings,
      );

      setSummary({
        weekHrs: demoWeekHrs,
        activeProjects: demoLogs.length
          ? new Set(demoLogs.map((g) => g.projectName).filter(Boolean)).size
          : 0,
      });

      setWeeklySnapshot({
        weekHrs: demoWeekHrs,
        overtimeHrs: demoPayrollTotals.overtimeHours.toFixed(1),
        estimatedGrossPay: `$${demoPayrollTotals.grossPay.toFixed(2)}`,
        weekRangeLabel: formatWeekRangeLabel(weekStartStr, weekEndStr),
      });

      setCrewSnapshot({
        crewHoursWeek: "142.5",
        unpaidEntries: 8,
      });

      setMetricsLoading(false);
      return;
    }

    if (currentUser?.supabase_user_id) {
      fetchDashboardData();
    } else if (currentUser) {
      setMetricsOffline(true);
    }
  }, [currentUser, directReportIds, showActiveCrew, settings]);

  async function fetchDashboardData() {
    devLog("DASHBOARD USER:", currentUser);
    if (!currentUser?.supabase_user_id) {
      devLog("NO USER ID YET");
      setMetricsOffline(true);
      setMetricsLoading(false);
      return;
    }

    if (isOffline()) {
      setMetricsOffline(true);
      setMetricsLoading(false);
      return;
    }

    setMetricsLoading(true);
    setMetricsOffline(false);
    try {
      // Date boundaries — org-configured payroll week, metrics through today
      const { weekStartStr, weekEndStr, todayStr } =
        getPayrollWeekBoundsFromSettings(new Date(), settings);
      const weekRangeLabel = formatWeekRangeLabel(weekStartStr, weekEndStr);

      // Rolling 7-day window (for trend)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(today.getDate() - 6);
      const sevenDaysAgoStr = toIsoDate(sevenDaysAgo);

      const organizationId = await resolveOperationalOrganizationId(
        currentUser,
        supabase,
        { directReportIds },
      );

      // ── Fetch time_logs for current week (personal) ───
      let personalWeekQuery = selectOperationalTimeLogs(
        supabase,
        "supabase_user_id, project_id, project_name, date_iso, total_hours, hourly_rate, start_time, end_time, work_completed, work_category, notes",
      )
        .eq("supabase_user_id", currentUser?.supabase_user_id)
        .gte("date_iso", weekStartStr)
        .lte("date_iso", todayStr)
        .order("date_iso", { ascending: false });

      personalWeekQuery = applyOperationalTimeLogOrgScope(
        personalWeekQuery,
        organizationId,
      );

      const { data: logs, error: logsError } = await personalWeekQuery;

      if (logsError) throw logsError;

      const timelogUserIds = await resolveScopedUserIdsForQuery(
        supabase,
        currentUser,
        CAPABILITY.TIMELOG_VIEW,
        { directReportIds },
      );
      const activeCrewUserIds = await resolveScopedUserIdsForQuery(
        supabase,
        currentUser,
        CAPABILITY.DASHBOARD_ACTIVE_CREW,
        { directReportIds },
      );

      // ── Fetch recent logs for activity feed (broader) ─
      let recentAll = [];
      let recentQuery = selectOperationalTimeLogs(
        supabase,
        "id, employee, project_name, date_iso, created_at, total_hours, start_time, end_time, work_completed, work_category, notes",
      )
        .order("created_at", { ascending: false })
        .limit(8);

      recentQuery = applyUserIdsToQuery(recentQuery, timelogUserIds);
      recentQuery = applyOperationalTimeLogOrgScope(recentQuery, organizationId);

      const { data, error } = await recentQuery;

      if (error) throw error;

      recentAll = data || [];

      /* if (recentError) throw recentError; */
      // ── Fetch active session ─────────────────────────
      let activeSessionQuery = supabase
        .from("active_sessions")
        .select("*")
        .eq("supabase_user_id", currentUser.supabase_user_id)
        .eq("status", "active");

      activeSessionQuery = applyOperationalActiveSessionOrgScope(
        activeSessionQuery,
        organizationId,
      );

      const { data: activeSessionData, error: activeSessionError } =
        await activeSessionQuery;
      devLog("ALL ACTIVE SESSION ROWS:", activeSessionData);
      devLog("ACTIVE SESSION ERROR:", activeSessionError);
      devLog("ACTIVE SESSION DATA:", activeSessionData);
      devLog("LOOKING FOR USER:", currentUser.supabase_user_id);

      if (activeSessionError) throw activeSessionError;

      setActiveSession(activeSessionData?.length ? activeSessionData[0] : null);

      if (showActiveCrew) {
        let crewQuery = supabase
          .from("active_sessions")
          .select(
            "session_id, supabase_user_id, project_name, session_start, break_active, status",
          )
          .eq("status", "active");

        crewQuery = applyUserIdsToQuery(crewQuery, activeCrewUserIds);
        crewQuery = applyOperationalActiveSessionOrgScope(crewQuery, organizationId);

        const { data: crewData, error: crewError } = await crewQuery;

        if (crewError) throw crewError;

        let userQuery = supabase
          .from("users")
          .select("supabase_user_id, employee_name");

        userQuery = applyUserIdsToQuery(userQuery, activeCrewUserIds);

        const { data: userData, error: userError } = await userQuery;

        if (userError) throw userError;

        const userMap = {};

        (userData || []).forEach((u) => {
          userMap[u.supabase_user_id] = u.employee_name;
        });

        const enrichedCrew = (crewData || []).map((row) => ({
          ...row,
          employee_name: userMap[row.supabase_user_id] || "Unknown Employee",
        }));

        setActiveCrew(enrichedCrew);

        let crewWeekQuery = selectOperationalTimeLogs(
          supabase,
          "total_hours, hourly_rate, supabase_user_id",
        )
          .gte("date_iso", weekStartStr)
          .lte("date_iso", todayStr);

        crewWeekQuery = applyUserIdsToQuery(crewWeekQuery, activeCrewUserIds);
        crewWeekQuery = applyOperationalTimeLogOrgScope(crewWeekQuery, organizationId);

        const { data: crewWeekLogs, error: crewWeekError } = await crewWeekQuery;

        if (crewWeekError) throw crewWeekError;

        const crewHoursWeek = sumHours(crewWeekLogs || []).toFixed(1);

        let unpaidQuery = selectOperationalTimeLogs(supabase, "*", {
          selectOptions: { count: "exact", head: true },
        }).eq("payroll_status", "unpaid");

        unpaidQuery = applyUserIdsToQuery(unpaidQuery, activeCrewUserIds);
        unpaidQuery = applyOperationalTimeLogOrgScope(unpaidQuery, organizationId);

        const { count: unpaidCount, error: payrollError } = await unpaidQuery;

        if (payrollError) throw payrollError;

        const scopedUnpaidCount = unpaidCount || 0;
        setUnpaidPayrollCount(scopedUnpaidCount);

        setCrewSnapshot({
          crewHoursWeek,
          unpaidEntries: scopedUnpaidCount,
        });
      } else {
        let selfUnpaidQuery = selectOperationalTimeLogs(supabase, "*", {
          selectOptions: { count: "exact", head: true },
        })
          .eq("payroll_status", "unpaid")
          .eq("supabase_user_id", currentUser.supabase_user_id);

        selfUnpaidQuery = applyOperationalTimeLogOrgScope(
          selfUnpaidQuery,
          organizationId,
        );

        const { count: unpaidCount, error: payrollError } = await selfUnpaidQuery;

        if (payrollError) throw payrollError;

        setUnpaidPayrollCount(unpaidCount || 0);
      }

      let allLogsQuery = selectOperationalTimeLogs(
        supabase,
        "project_id, project_name, total_hours",
      );

      allLogsQuery = applyUserIdsToQuery(allLogsQuery, timelogUserIds);
      allLogsQuery = applyOperationalTimeLogOrgScope(allLogsQuery, organizationId);

      const { data: allLogs, error: allLogsError } = await allLogsQuery;

      if (allLogsError) throw allLogsError;

      // ── Weekly snapshot (Sun–today) ───────────────────

      const weekLogs = logs || [];

      const weekPayrollTotals = computePayrollPeriodTotals(weekLogs, settings);
      const weekHrs = weekPayrollTotals.totalHours.toFixed(1);

      const activeProjects = new Set(
        weekLogs.filter((l) => l.project_name).map((l) => l.project_name),
      ).size;

      setSummary({
        weekHrs,
        activeProjects,
      });

      setWeeklySnapshot({
        weekHrs,
        overtimeHrs: weekPayrollTotals.overtimeHours.toFixed(1),
        estimatedGrossPay: `$${weekPayrollTotals.grossPay.toFixed(2)}`,
        weekRangeLabel,
      });

      // ── CARD 2: 7-day trend ───────────────────────────
      let trendQuery = selectOperationalTimeLogs(
        supabase,
        "date_iso, total_hours",
      )
        .eq("supabase_user_id", currentUser?.supabase_user_id)
        .gte("date_iso", sevenDaysAgoStr)
        .lte("date_iso", todayStr);

      trendQuery = applyOperationalTimeLogOrgScope(trendQuery, organizationId);

      const { data: trendLogs, error: trendError } = await trendQuery;

      if (trendError) throw trendError;
      const trendDays = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(today.getDate() - i);
        const ds = toIsoDate(d);
        const dayLogs = (trendLogs || []).filter((l) => l.date_iso === ds);
        const hrs = parseFloat(
          dayLogs
            .reduce((s, l) => s + Number(l.total_hours || 0), 0)
            .toFixed(1),
        );
        trendDays.push({ day: getDayLabel(ds), hrs });
      }
      setWeekTrend(trendDays);

      // ── CARD 3: Project hours (all-time) ──────────────
      const projectHourAggregates = aggregateProjectHoursFromLogs(allLogs || []);
      const projectIds = projectHourAggregates
        .map((entry) => entry.project_id)
        .filter(Boolean);
      const scopedProjects = await loadScopedProjectsByIds(
        supabase,
        currentUser,
        projectIds,
        { directReportIds },
      );
      const reconciledProjectHours = reconcileProjectHoursRows(
        projectHourAggregates,
        scopedProjects,
        { restrictToActiveProjects: isEmployee(currentUser) },
      );
      setProjectHours(reconciledProjectHours.slice(0, 6));

      // ── CARD 4: Recent activity ───────────────────────
      const recent = (recentAll || []).slice(0, 20).map((l) => ({
        timeLogId: l.id ?? null,
        dateIso: l.date_iso,
        category: l.work_category || "",
        employeeName: l.employee || "",
        workCompleted: l.work_completed || l.work_category || "Work session",
        notes: l.notes || "",
        projectName: l.project_name || null,
        hrs: Number(l.total_hours || 0).toFixed(1),
        startTime: fmtTime(l.start_time),
        endTime: fmtTime(l.end_time),
        shiftLabel: formatSessionShiftLabel(l.start_time, l.end_time),
      }));
      setRecentLogs(recent);
    } catch (err) {
      console.error("Dashboard fetch error:", err);
      setMetricsOffline(true);
    } finally {
      setMetricsLoading(false);
      setSessionMaterialsRefreshKey((key) => key + 1);
    }
  }

  useEffect(() => {
    function handleSessionStateChanged() {
      if (currentUser?.isGuest) {
        return;
      }

      if (currentUser?.supabase_user_id) {
        fetchDashboardData();
      }
    }

    window.addEventListener(
      APP_SESSION_STATE_CHANGED_EVENT,
      handleSessionStateChanged,
    );

    return () => {
      window.removeEventListener(
        APP_SESSION_STATE_CHANGED_EVENT,
        handleSessionStateChanged,
      );
    };
  }, [currentUser]);

  const dateStr = now.toLocaleDateString("en-CA", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  const onBreakCount = activeCrew.filter((crew) => crew.break_active).length;
  const activeCrewListTitle =
    activeCrewListFilter === "on_break" ? "On Break" : "Active Crew";
  const activeCrewListRows =
    activeCrewListFilter === "on_break"
      ? activeCrew.filter((crew) => crew.break_active)
      : activeCrew;
  const activeCrewListEmptyMessage =
    activeCrewListFilter === "on_break"
      ? "No crew members on break."
      : "No active employees.";

  const renderWeeklySnapshotCard = () =>
    typeof onOpenTimeLogs === "function" ? (
      <button
        type="button"
        className="ops-summary-card-button"
        onClick={onOpenTimeLogs}
        style={{
          display: "block",
          width: "100%",
          padding: 0,
          margin: 0,
          border: "none",
          background: "transparent",
          color: "inherit",
          font: "inherit",
          textAlign: "left",
          cursor: "pointer",
        }}
        aria-label="Open Time Logs Hub"
      >
        <SnapshotMetricCard
          title="Weekly Snapshot"
          subtitle={weeklySnapshot.weekRangeLabel}
          metrics={[
            {
              label: "Hours This Week",
              value: weeklySnapshot.weekHrs,
            },
            {
              label: "Overtime Hours",
              value: weeklySnapshot.overtimeHrs,
            },
            {
              label: "Estimated Gross Pay",
              value: weeklySnapshot.estimatedGrossPay,
            },
          ]}
        />
      </button>
    ) : (
      <SnapshotMetricCard
        title="Weekly Snapshot"
        subtitle={weeklySnapshot.weekRangeLabel}
        metrics={[
          {
            label: "Hours This Week",
            value: weeklySnapshot.weekHrs,
          },
          {
            label: "Overtime Hours",
            value: weeklySnapshot.overtimeHrs,
          },
          {
            label: "Estimated Gross Pay",
            value: weeklySnapshot.estimatedGrossPay,
          },
        ]}
      />
    );

  const renderProjectHoursCard = () => (
    <SnapshotMetricCard
      title="Project Hours"
      onTitleClick={() => onOpenProjectHours?.()}
      emptyMessage="No project data yet."
      leadingContent={
        projectHours.length > 0 ? (
          <ProjectHoursDonutChart projects={projectHours} />
        ) : null
      }
      metrics={projectHours.map((p) => ({
        key: p.project_id || p.name,
        label: p.name,
        value: formatHoursMinutes(p.hrs),
        onClick: () => {
          onOpenProjectHoursRow?.({
            project_id: p.project_id,
            name: p.name,
            hrs: p.hrs,
            created_by: p.created_by,
          });
        },
      }))}
    />
  );

  const renderRecentActivityCard = () => (
    <SnapshotMetricCard
      title="Recent Activity"
      subtitle="Latest Entries"
      emptyMessage="No recent logs."
      metrics={recentLogs.map((a, i) => ({
        key: `${a.dateIso}-${a.startTime}-${i}`,
        label: a.workCompleted,
        subtitle: [a.employeeName, a.projectName].filter(Boolean).join(" · "),
        value: formatHoursMinutes(a.hrs),
        onClick: () => {
          devLog("ACTIVITY CLICKED:", a);
          if (a.timeLogId != null && typeof onOpenTimeLog === "function") {
            onOpenTimeLog(a.timeLogId);
            return;
          }
          window.scrollTo({
            top: 0,
            behavior: "smooth",
          });
          setSelectedActivity(a);
        },
      }))}
    />
  );

  const renderKpiWidget = (widgetId) => {
    switch (widgetId) {
      case "weekly_snapshot":
        return renderWeeklySnapshotCard();
      case "project_hours":
        return renderProjectHoursCard();
      case "recent_activity":
        return renderRecentActivityCard();
      default:
        return null;
    }
  };

  const mobileKpiWidgetIds = useMemo(() => {
    const order = loadDashboardWidgetOrder(currentUser);
    // Legacy Crew Snapshot card removed — superseded by the Active Now /
    // On Break KPI tiles above, which reuse the same showActiveCrew gate.
    return getOrderedKpiWidgetIds(order).filter((id) => id !== "crew_snapshot");
  }, [currentUser, weeklySnapshot, projectHours, recentLogs]);

  return (
    <>
      <style>
        {`
        @keyframes ashleyRingPulse {
          0% {
            transform: scale(1);
            opacity: 0.85;
          }

          50% {
            transform: scale(1.03);
            opacity: 1;
          }

          100% {
            transform: scale(1);
            opacity: 0.85;
          }
        }
      `}
      </style>

      <div
        ref={carouselRef}
        className="kpi-screen cp-desktop-page"
        style={carouselTouchStyle}
      >
        {isDesktop ? (
          <DesktopCommandDashboard
            currentUser={currentUser}
            userName={userName}
            activeSession={activeSession}
            activeCrew={activeCrew}
            showActiveCrew={showActiveCrew}
            recentLogs={recentLogs}
            quickActions={(quickActions || []).filter((action) => !action.capability || can(action.capability))}
            onOpenAshleyHome={onOpenAshleyHome}
            onOpenUserPreferences={onOpenUserPreferences}
            onOpenGuide={() => {
              if (productEducation) {
                productEducation.openEducationMenu();
                return;
              }
              runLegacyDashboardGuide();
            }}
            onOpenSession={onOpenSession}
            onOpenCrew={setActiveCrewListFilter}
            onOpenPlanning={onOpenPlanning}
            onOpenProjects={onOpenProjects}
            onOpenProjectsWithOverdueTasks={onOpenProjectsWithOverdueTasks}
            onOpenProjectRow={onOpenProjectHoursRow}
            onOpenEstimateHubWithStatus={onOpenEstimateHubWithStatus}
            onOpenAccountsReceivable={onOpenAccountsReceivable}
            onOpenOverdueInvoices={onOpenOverdueInvoices}
            onOpenAccountsPayable={onOpenAccountsPayable}
            onOpenFinance={onOpenFinance}
            onOpenTimeLogs={onOpenTimeLogs}
            onOpenTimeLog={onOpenTimeLog}
            onOpenTimeLogsThisWeek={onOpenTimeLogsThisWeek}
            onArrangingChange={setHomeArranging}
          />
        ) : (
          <>
        <div className="kpi-header kpi-header--compact kpi-header--mobile-dash">
          {missionRecording && (
            <img
              src={ashleyGuide}
              alt="Ashley Sterling"
              onClick={() => {
                setMissionRecording(false);
              }}
              style={{
                position: "absolute",
                top: "-10px",
                right: "1px",
                width: "180px",
                height: "auto",
                zIndex: 99999,
                pointerEvents: "none",
              }}
            />
          )}

          {/* ── Persistent top bar: brand (= Ashley Home launcher) + icons ── */}
          <div className="kpi-topbar">
            <div
              ref={ashleyHomeLauncherRef}
              className="kpi-topbar__brand crewpilot-ashley-home-launcher ios-interaction-guard"
              onClick={() => onOpenAshleyHome?.()}
              role="button"
              tabIndex={0}
              aria-label="Open Ashley Home"
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onOpenAshleyHome?.();
                }
              }}
            >
              <img
                src="/logo512.png"
                alt="CrewPilot"
                className="kpi-topbar__brand-mark"
                draggable={false}
              />
              <span className="kpi-topbar__brand-name">CrewPilot</span>
            </div>
            <div className="kpi-topbar__status">
              <SyncStatusIndicator actor={currentUser} />
            </div>

            <div className="kpi-topbar__actions">
              <button
                type="button"
                className="kpi-topbar__icon-btn guide-help-button"
                aria-label="Guide"
                title="Guide"
                onClick={() => {
                  if (productEducation) {
                    productEducation.openEducationMenu();
                    return;
                  }
                  runLegacyDashboardGuide();
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M9.5 9a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2-2.5 3.5" />
                  <path d="M12 17h.01" />
                </svg>
              </button>

              {onOpenUserPreferences ? (
                <button
                  type="button"
                  className="kpi-topbar__icon-btn guide-preferences-button"
                  aria-label="Settings"
                  title="Settings"
                  onClick={onOpenUserPreferences}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1.08 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
                  </svg>
                </button>
              ) : null}

              {MIC_SMOKE_TEST_ENABLED && (
                <button
                  type="button"
                  className="kpi-topbar__icon-btn mic-test-button"
                  onClick={runMicRecognitionSmokeTest}
                  aria-label="Test Microphone"
                  title="Test Microphone"
                >
                  🎤
                </button>
              )}
            </div>
          </div>

          {homeArranging ? null : (
          <div style={{ display: "flex", justifyContent: "center", padding: "4px 0 2px" }}>
            <HomeSwipeAffordance
              face="home"
              onActivate={() => onCarouselNavigate?.("left")}
            />
          </div>
          )}

        </div>

        <DesktopCommandDashboard
          variant="mobile"
          currentUser={currentUser}
          userName={userName}
          activeSession={activeSession}
          activeCrew={activeCrew}
          showActiveCrew={showActiveCrew}
          recentLogs={recentLogs}
          quickActions={(quickActions || []).filter((action) => !action.capability || can(action.capability))}
          onOpenAshleyHome={onOpenAshleyHome}
          onOpenSession={onOpenSession}
          onOpenCrew={setActiveCrewListFilter}
          onOpenPlanning={onOpenPlanning}
          onOpenProjects={onOpenProjects}
          onOpenProjectsWithOverdueTasks={onOpenProjectsWithOverdueTasks}
          onOpenProjectRow={onOpenProjectHoursRow}
          onOpenEstimateHubWithStatus={onOpenEstimateHubWithStatus}
          onOpenAccountsReceivable={onOpenAccountsReceivable}
          onOpenOverdueInvoices={onOpenOverdueInvoices}
          onOpenAccountsPayable={onOpenAccountsPayable}
          onOpenFinance={onOpenFinance}
          onOpenTimeLogs={onOpenTimeLogs}
          onOpenTimeLog={onOpenTimeLog}
          onOpenTimeLogsThisWeek={onOpenTimeLogsThisWeek}
          onArrangingChange={setHomeArranging}
        />

        <div className="cmd-mobile-entry">
          <GoogleVisibilityEntryCard
            actor={currentUser}
            onOpen={onOpenGoogleVisibility}
          />
        </div>
          </>
        )}
        {selectedActivity && (
          <ViewportPortal>
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.8)",
              zIndex: 9999,
              padding: "20px",
              overflowY: "auto",
            }}
            onClick={() => setSelectedActivity(null)}
          >
            <div
              className="kpi-chart-card"
              onClick={(e) => e.stopPropagation()}
            >
              <h2>Activity Details</h2>

              <div className="section-heading">PROJECT</div>
              <div style={{ marginTop: "-8px", marginBottom: "16px" }}>
                {selectedActivity.projectName}
              </div>

              <div className="section-heading">WORK TYPE</div>
              <div style={{ marginTop: "-8px", marginBottom: "16px" }}>
                {selectedActivity.category || "—"}
              </div>

              <div className="section-heading">DATE</div>
              <div style={{ marginTop: "-8px", marginBottom: "16px" }}>
                {fmtDateIso(selectedActivity.dateIso)}
              </div>

              <div className="section-heading">SHIFT</div>
              <div style={{ marginTop: "-8px", marginBottom: "16px" }}>
                {selectedActivity.shiftLabel && selectedActivity.shiftLabel !== "—"
                  ? selectedActivity.shiftLabel
                  : selectedActivity.startTime && selectedActivity.endTime
                  ? `${selectedActivity.startTime} → ${selectedActivity.endTime}`
                  : "—"}
              </div>

              <div className="section-heading">TOTAL HOURS</div>
              <div style={{ marginTop: "-8px", marginBottom: "16px" }}>
                {formatHoursMinutes(selectedActivity.hrs)}
              </div>

              <hr style={{ margin: "16px 0 24px 0" }} />

              <div className="section-heading">WORK COMPLETED</div>
              <div style={{ marginTop: "-8px", marginBottom: "16px" }}>
                {selectedActivity.workCompleted}
              </div>

              <div className="section-heading">NOTES</div>
              <div style={{ marginTop: "-8px", marginBottom: "16px" }}>
                {selectedActivity.notes}
              </div>

              <button
                className="kpi-back-btn"
                onClick={() => setSelectedActivity(null)}
              >
                Close
              </button>
            </div>
          </div>
          </ViewportPortal>
        )}
        <ActiveCrewListModal
          open={activeCrewListFilter !== null}
          title={activeCrewListTitle}
          crewList={activeCrewListRows}
          emptyMessage={activeCrewListEmptyMessage}
          onSelectCrew={setSelectedCrew}
          onClose={() => setActiveCrewListFilter(null)}
        />
        {selectedCrew && (
          <ViewportPortal>
          <div
            className="kpi-active-crew-details-backdrop"
            onClick={() => setSelectedCrew(null)}
          >
            <div
              className="kpi-chart-card kpi-active-crew-details-panel"
              onClick={(e) => e.stopPropagation()}
            >
              <h2>Active Session</h2>

              <div className="section-heading">EMPLOYEE</div>

              <div
                style={{
                  marginTop: "-8px",
                  marginBottom: "16px",
                }}
              >
                {selectedCrew.employee_name}
              </div>

              <div className="section-heading">PROJECT</div>

              <div
                style={{
                  marginTop: "-8px",
                  marginBottom: "16px",
                }}
              >
                {selectedCrew.project_name}
              </div>

              <div className="section-heading">STATUS</div>

              <div
                style={{
                  marginTop: "-8px",
                  marginBottom: "16px",
                }}
              >
                {selectedCrew.break_active ? "On Break" : "Working"}
              </div>

              <div className="section-heading">STARTED</div>

              <div
                style={{
                  marginTop: "-8px",
                  marginBottom: "16px",
                }}
              >
                {new Date(selectedCrew.session_start).toLocaleString("en-CA", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </div>

              <div className="section-heading">ELAPSED</div>

              <div
                style={{
                  marginTop: "-8px",
                  marginBottom: "16px",
                }}
              >
                {getElapsedTime(selectedCrew.session_start)}
              </div>

              {showActiveCrew && selectedCrew.session_id && (
                <ActiveSessionMaterialsSection
                  sessionId={selectedCrew.session_id}
                  showPricing={canViewMaterialPricing}
                  refreshKey={sessionMaterialsRefreshKey}
                />
              )}

              <button
                className="kpi-back-btn"
                onClick={() => setSelectedCrew(null)}
              >
                Close
              </button>
            </div>
          </div>
          </ViewportPortal>
        )}
        
        
      </div>
    </>
  );
}
